import { operatorApiFetch } from '@services/operator/apiService'

export const fulfillmentService = {
  fetchList: async (params) => {
    try {
      const response = await operatorApiFetch('fulfillments', {
        method: 'GET',
        params: params
      })
      const data = await response.json()
      if (data && data.data) {
        return {
          success: true,
          data: data.data,
          // Support anchor pagination
          has_more: data.has_more,
          since_id: data.since_id
        }
      }
      return { success: false, message: 'Invalid response format' }
    } catch (error) {
      console.error('Fetch fulfillments error:', error)
      return { success: false, message: error.message }
    }
  },

  getRecords: async (params) => {
    const response = await operatorApiFetch('fulfillments/get_records', {
      method: 'POST',
      body: JSON.stringify(params)
    })
    return response.json()
  },

  getIds: async (params) => {
    const response = await operatorApiFetch('fulfillments/get_ids', {
      method: 'POST',
      body: JSON.stringify(params)
    })
    return response.json()
  },

  get: async (id) => {
    const response = await operatorApiFetch(`fulfillments/${id}`, {
      method: 'GET'
    })
    return response.json()
  },

  splitPreview: async (id, itemIds) => {
    try {
      const response = await operatorApiFetch(`fulfillments/${id}/split_preview`, {
        method: 'POST',
        body: { fulfillment_item_ids: itemIds }
      })
      const data = await response.json()
      return data?.success
        ? { success: true, data: data.data }
        : { success: false, message: data?.message }
    } catch (error) {
      console.error('Split preview error:', error)
      return { success: false, message: error.message }
    }
  },

  // 取得單一 fulfillment 的合併後影片清單（提供給 shared/VideoSection 使用，
  // 與 orderService.getVideos 同 shape）
  getVideos: async (id) => {
    const response = await operatorApiFetch(`fulfillments/${id}/videos`, {
      method: 'GET'
    })
    return response.json()
  },

  // 取得單一 fulfillment 的錄影 session 診斷資訊（含 GCS chunk 數量）
  getRecordingSessions: async (id) => {
    const response = await operatorApiFetch(`fulfillments/${id}/recording_sessions`, {
      method: 'GET'
    })
    return response.json()
  },

  // 強制合併卡住的 RecordingSession（rescue 工具，與 orderService 同實作）
  forceFinalizeRecordingSession: async (sessionId) => {
    const response = await operatorApiFetch(`recording_sessions/${sessionId}/force_finalize`, {
      method: 'POST'
    })
    return response.json()
  },

  split: async (id, itemIds, warehouseId, shippingTypeId, desiredAmounts) => {
    try {
      const body = { fulfillment_item_ids: itemIds }
      if (warehouseId) body.warehouse_id = warehouseId
      if (shippingTypeId) body.shipping_type_id = shippingTypeId
      // desired_amounts 結構：[new_fulfillment_total, source_remaining_total]
      // backend 會 validate Σ == fulfillment.total_amount，不符就 raise SPLIT_AMOUNTS_NOT_CONSERVED
      if (desiredAmounts) body.desired_amounts = desiredAmounts

      const response = await operatorApiFetch(`fulfillments/${id}/split`, {
        method: 'POST',
        body
      })
      const data = await response.json()

      // backend 422 也會回 JSON（{ success: false, message: <error_code>, interpolations }）— 必須檢查 success / response.ok，
      // 不能只看「data 非空」就當成功。message 是 error code（per render_error 約定），FE 用 t(`errors.${code}`) 翻譯。
      if (!response.ok || data?.success === false) {
        return { success: false, message: data?.message, interpolations: data?.interpolations }
      }
      return { success: true, data }
    } catch (error) {
      console.error('Split fulfillment error:', error)
      return { success: false, message: error.message }
    }
  },

  // 取得可合併的出貨單分組
  getMergeableGroups: async (params = {}) => {
    const queryParams = new URLSearchParams()
    if (params.merchant_id) queryParams.append('merchant_id', params.merchant_id)

    const response = await operatorApiFetch(`fulfillments/mergeable_groups?${queryParams.toString()}`, {
      method: 'GET'
    })
    return response.json()
  },

  merge: async (ids) => {
    try {
      const response = await operatorApiFetch('fulfillments/merge', {
        method: 'POST',
        body: { ids }
      })
      const data = await response.json()
      if (!response.ok) {
        return { success: false, message: data?.message || data?.error || 'Merge failed' }
      }
      if (data) {
        return { success: true, data }
      }
      return { success: false }
    } catch (error) {
      console.error('Merge fulfillments error:', error)
      return { success: false, message: error.message }
    }
  },

  // 完成出貨（跳過 QC 掃碼）
  complete: async (fulfillmentId) => {
    const response = await operatorApiFetch(`fulfillments/${fulfillmentId}/complete`, {
      method: 'POST'
    })
    return response.json()
  },

  // 列印托運單標籤（ShippingAccount 由後端 ShippingAccountResolver 自動選擇）
  // reprint = true：重印按鈕觸發，後端 source_system 會一律軟刪重建（避免沿用 label）
  printLabel: async (fulfillmentId, quantity, { reprint = false } = {}) => {
    const response = await operatorApiFetch(`fulfillments/${fulfillmentId}/print_label`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ quantity, reprint }),
    })
    return response.json()
  },

  changeShippingType: async (id, shippingTypeId) => {
    try {
      const response = await operatorApiFetch(`fulfillments/${id}/change_shipping_type`, {
        method: 'POST',
        body: { shipping_type_id: shippingTypeId }
      })
      const data = await response.json()
      if (!response.ok) {
        return { success: false, message: data?.message || 'Change shipping type failed' }
      }
      return { success: true, data }
    } catch (error) {
      console.error('Change shipping type error:', error)
      return { success: false, message: error.message }
    }
  }
}
